import {DataSourceApi} from '../api/data-source.api';
import {DataSourceState} from '../state/data-source.state';
import {Router} from '@angular/router';
import {ToastrWrapperService} from '../../shared/services/toastr-wrapper.service';
import {map} from 'rxjs/operators';
import {DataSource} from '../models/data-source.modol';
import {Observable, forkJoin} from 'rxjs';
import {RelationData} from '../models/relationData.model';
import {computed, Injectable, Optional} from '@angular/core';
import {Column} from '../models/column.model';
import {ScheduleAsset} from '../models/scheduleAsset.model';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {ScheduleHistory} from '../models/scheduleHistory.model';
import {ColumnsType} from '../models/columns-type.model';
import {WebServiceConnection} from '../models/web-service-connection.model';
import {DataSourceRowNumber} from '../models/data-source-row-number.type';
import {Model} from '../models/model.model';
import {Relation} from '../models/relation.model';
import {Submit} from '../../data-modelling/models/submit.enum';
import {RelationState} from '../models/relation-state.type';
import {SubmitType} from '../../shared/models/submit.type';
import {CalculateColumnModel} from '../models/calculate-column.model';
import {PermissionsService} from '../../shared/components/permissions/services/permissions.service';
import {UserFilterService} from '../../shared/services/user-filter.service';
import {UserFilter} from '../../shared/models/user-filter.model';
import {UserOrGroup} from '../../shared/models/userOrGroup.model';
import {AbstractFacade} from '../../shared/models/abstract-facade';
import {UserInUserFilter} from '../../shared/models/userInUserfilter.model';
import {UserFilterDetails} from '../../shared/models/userFilterDetails.model';
import {ColumnList} from '../models/column-list.model';
import {DataSourceConditionalColumn} from '../models/conditional-column.model';
import {PreviewDataSourceInfo} from '../models/preview-data-source-info.model';
import {MessageDialogComponent} from '../../shared/components/message-dialog/message-dialog.component';
import {DateSourceErrorMessageDialogComponent}
    from '../components/dialogs/date-source-error-message-dialog/date-source-error-message-dialog.component';
import {DataSourceWarningMessageDialogComponent}
    from '../components/dialogs/data-source-warning-message-dialog/data-source-warning-message-dialog.component';
import {DateSourceConflictDialogComponent}
  from '../components/dialogs/date-source-conflict-dialog/date-source-error-message-dialog.component';
import {ColumnDataSourceUpdate} from '../models/confilict-column.model';
import {CustomDateColumns} from '../models/custom-date-columns.model';
import {ProcessingDataSource} from '../models/processing-data-source.model';
import {DataSourceUploadProgress} from '../models/processing-data-source.model';
import {DataSourceUploadProgressService} from '../services/data-source-upload-progress.service';

@Injectable()
export class DataSourceFacade extends AbstractFacade{

  readonly state = computed(()=>({
    isUserFilterDetailsError: this.dataSourceState.isUserFilterDetailsError()
  }));

  constructor(
    @Optional() private dataSourceApi: DataSourceApi,
    @Optional() private dataSourceState: DataSourceState,
    @Optional() private router: Router,
    @Optional() private toastr: ToastrWrapperService,
    @Optional() private permissionsService: PermissionsService,
    @Optional() private userFilterService: UserFilterService,
    @Optional() private dialogService: NbDialogService,
    @Optional() private progressService: DataSourceUploadProgressService
  ) {
      super();
  }

  // mainDatasource and relations
  loadDataSource(workspaceId: string, dataSourceId: string,
                 pageNumber: number, pageSize: number, needCount: boolean, searchUserFilterKey: string): void {
    this.dataSourceState.setLoadingDataSource(true);
    this.dataSourceState.updateMainDataSource(null,null);
    this.permissionsService.loadDataSource(workspaceId,dataSourceId).subscribe(
      dataSource => {
        dataSource.showAllFields = true;
        const hasScheduleHistoryPermission = dataSource.permissions.get('ViewSchedule').isChecked &&
            (dataSource.dbConnectionType === 'WebService' ||
                dataSource.dbConnectionType === 'MicrosoftSQLServer' ||
                dataSource.dbConnectionType === 'Oracle');
        this.loadDataSourceTableContent(workspaceId, dataSourceId, 'Hundred',
          dataSource.permissions.get('View').isChecked);
        this.loadDataSourceRelations(workspaceId, dataSourceId, dataSource);
        this.loadDataSourceUserFilters(workspaceId, dataSourceId, searchUserFilterKey, pageNumber, pageSize, true,
            dataSource.permissions.get('UserFilter').isChecked);
        this.loadDatasourceColumnsDataTypes(workspaceId, dataSourceId,dataSource.permissions.get('Edit').isChecked);
        this.loadScheduleHistory(workspaceId, dataSourceId, pageNumber, pageSize, needCount,hasScheduleHistoryPermission);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadDataSourceTableContent(workspaceId: string, dataSourceId: string, rowNumber: string,
                             permissionViewIsChecked: boolean = true): void {
    if(permissionViewIsChecked) {
      this.dataSourceApi.getDataSourceTableContent(workspaceId, dataSourceId, rowNumber).
      pipe(map(res => res.data.columns)).subscribe(
        (columns: Column[]) => {
          this.dataSourceState.updateDataSourceTableContent(columns);
          this.dataSourceState.loadingDataSourceTable = false;
          this.dataSourceState.checkDataSourceIsLoaded();
          // this.loadProcessingDataSources();
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
      );
    } else {
      this.dataSourceState.updateDataSourceTableContent([]);
      this.dataSourceState.loadingDataSourceTable = false;
      this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  loadDatasourceColumnsDataTypes(workspaceId: string, dataSourceId: string,
                                 permissionEditIsChecked: boolean = true): void {
    if(permissionEditIsChecked) {
      this.dataSourceApi.getDatasourceColumnsDataTypes(workspaceId,dataSourceId).
      pipe(map(res => res.data.columnsTypes)).subscribe(
        (columnsType: ColumnsType) => {
          this.dataSourceState.updateColumnsDataType(columnsType);
          this.dataSourceState.loadingDataSourceColumnsDataTypes = false;
          this.dataSourceState.checkDataSourceIsLoaded();
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
      );
    } else {
      this.dataSourceState.updateColumnsDataType([]);
      this.dataSourceState.loadingDataSourceColumnsDataTypes = false;
      this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  loadDataSourceRelations(workspaceId: string, dataSourceId: string, dataSource: DataSource): void  {
    if(dataSource.permissions.get('Relations').isChecked) {
      this.dataSourceApi.getDataSourceRelations(workspaceId,dataSourceId).pipe(map(res => res.data)).
      subscribe(
        (relations: RelationData) => {
          this.dataSourceState.updateMainDataSource(dataSource,relations);
          this.dataSourceState.loadingRelations = false;
          this.dataSourceState.checkDataSourceIsLoaded();
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
      );
    } else {
      this.dataSourceState.updateMainDataSource(dataSource);
      this.dataSourceState.loadingRelations = false;
      this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  updateDatasourceColumnsDataTypes(workspaceId: string, dataSourceId: string, columnsTypes: ColumnsType): void {
      this.setSubmitState('submitting');
    this.dataSourceApi.updateDatasourceColumnsDataTypes(workspaceId, dataSourceId, columnsTypes).subscribe(
      () => {
        this.loadDataSourceTableContent(workspaceId, dataSourceId, 'Hundred');
        this.dataSourceState.updateColumnsDataType(columnsTypes);
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.setSubmitState('submittedWithError');
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.setSubmitState('submitted');
      },
    );
  }

  checkUpdateDatasourceColumnsDataTypes(workspaceId: string, dataSourceId: string, columnsTypes: ColumnsType): void {
      this.setSubmitState('submitting');
      this.dataSourceApi.checkUpdateDatasourceColumnDataTypes(workspaceId, dataSourceId, columnsTypes).subscribe(
          (response) => {
              if(response?.data.hasError) {
                  this.dialogService.open(DataSourceWarningMessageDialogComponent,
                      {
                          context: {
                              warnings: response.data.warnings,
                              title: 'تغییر نوع ستون',
                              columnsTypes,
                              type: 'updateDatasourceColumnTypes',
                          },
                      });
                  this.setSubmitState('submittedWithError');
              } else {
                  this.updateDatasourceColumnsDataTypes(workspaceId, dataSourceId, columnsTypes);
              }
          },
          (errorRes) => {
              this.setSubmitState('submittedWithError');
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateColumnDataTypes(columnTypes: ColumnsType): void{
    this.dataSourceState.updateColumnsDataType(columnTypes);
  }

  updateDatasourceData(workspaceId: string, dataSourceId: string, columns: ColumnDataSourceUpdate[] = []) {
    this.setSubmitState('submitting');
    this.dataSourceState.setIsUpdatingDatasource(true);
    this.dataSourceApi.updateDatasourceData(workspaceId, dataSourceId, columns).subscribe(
      () => {
        this.dataSourceState.setIsUpdatingDatasource(false);
        
        this.progressService.closeModal();
        this.progressService.forceOpenModal();
        this.progressService.toggle(true);
        
        this.setSubmitState('submitted');
        this.loadDataSource(workspaceId, dataSourceId, 1, 9 , true, null)

      },
      (errorRes) => {
        if (errorRes.error.showPopUp) {
          this.dialogService.open(MessageDialogComponent,
            {
              closeOnBackdropClick: false,
              context: {
                message: 'به‌روزرسانی انجام نشد. پس از بررسی جدول، دوباره امتحان کنید.',
                errorMessage: errorRes.error.message, icon: 'error', title: 'ناموفق',
              },
            });
        } else {
          this.toastr.showError(errorRes.error.message);
        }
        this.dataSourceState.setIsUpdatingDatasource(false);
        this.setSubmitState('submittedWithError');
      },
      () => {
        this.dataSourceState.setIsUpdatingDatasource(false);
          this.setSubmitState('notSubmitted');
      },
    );
  }

  stopSchedule(workspaceId: string, dataSourceId: string, scheduleId: string) {
    this.dataSourceApi.stopSchedule(workspaceId, dataSourceId, scheduleId).subscribe(
      res=>{
        this.dataSourceState.setIsUpdatingDatasource(false);
        window.location.reload();
        this.setSubmitState('notSubmitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.dataSourceState.setIsUpdatingDatasource(false);
        this.setSubmitState('notSubmitted');
      },
    );
  }

  stopManualUpdate(workspaceId: string, dataSourceId: string) {
    this.dataSourceApi.stopManualUpdate(workspaceId, dataSourceId).subscribe(
      res=>{
        this.dataSourceState.setIsUpdatingDatasource(false);
        window.location.reload();
        this.setSubmitState('notSubmitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.dataSourceState.setIsUpdatingDatasource(false);
        this.setSubmitState('notSubmitted');
      },
    );
  }

  checkUpdateDataSourceDataSqlReplace(workspaceId: string, dataSourceId: string) {
      this.dataSourceApi.checkUpdateDataSourceData(workspaceId, dataSourceId).subscribe(
          (response) => {
            this.updateDatasourceData(workspaceId, dataSourceId);
          },
          (errorRes) => {
            if (errorRes.error.showPopUp) {
              this.dialogService.open(MessageDialogComponent,
                {
                  closeOnBackdropClick: false,
                  context: {
                    message: errorRes.error.message, icon: 'error', title: 'ناموفق',
                  },
                });
            } else {
              this.toastr.showError(errorRes.error.message);
            }
          },
      );
  }

  checkUpdateDataSourceData(workspaceId: string, dataSourceId: string) {
    this.dataSourceState.setIsUpdatingDatasource(true);
      this.dataSourceApi.checkUpdateDataSourceData(workspaceId, dataSourceId).subscribe(
          (response) => {
            if (response?.data.hasError) {
              if (response.data.columns.length > 0) {
                this.dialogService.open(DateSourceConflictDialogComponent,
                  {
                    context: {
                      warnings: response.data.warnings,
                      columns: response.data.columns,
                    },
                  });
              } else {
                this.dialogService.open(DateSourceErrorMessageDialogComponent,
                  {
                    context: {
                      message: response.data.errorMessage,
                      warnings: response.data.warnings,
                      title: 'ناموفق',
                    },
                  });
              }
              this.dataSourceState.setIsUpdatingDatasource(false);
            } else {
              this.updateDatasourceData(workspaceId, dataSourceId);
            }
          },
          (errorRes) => {
            if (errorRes.error.showPopUp) {
              this.dialogService.open(MessageDialogComponent,
                {
                  closeOnBackdropClick: false,
                  context: {
                    message: errorRes.error.message, icon: 'error', title: 'ناموفق',
                  },
                });
            } else {
              this.toastr.showError(errorRes.error.message);
            }
            this.dataSourceState.setIsUpdatingDatasource(false);
          },
      );
  }

  checkDeleteDataSource(workspaceId: string, dataSources: string[]) {
    this.dataSourceState.setUsedDashboards([]);
    this.dataSourceApi.checkDeleteDataSource(workspaceId, dataSources).subscribe(
      res=>{
        const {dashboards} = res.data;
        this.dataSourceState.setUsedDashboards(dashboards);
      },
      error => this.toastr.showError(),
    );
  }

  getUsedDashboards$(): Observable<string[]>{
    return this.dataSourceState.getUsedDashboards$();
  }

  getUsedDashboardsValue(): string[]{
    return this.dataSourceState.getUsedDashboardsValue();
  }

  updateDataSourceNameState(name: string): void {
      this.dataSourceState.setDataSourceName(name);
  }

  getMainDataSource$(): Observable<DataSource> {
    return this.dataSourceState.getMainDataSource$();
  }

  getTableContent$(): Observable<Column[]> {
    return this.dataSourceState.getTableContent$();
  }

  getColumnsType$(): Observable<ColumnsType> {
    return this.dataSourceState.getColumnsType$();
  }

  // user filter
  loadDataSourceUserFilters(workspaceId: string, dataSourceId: string, searchKey: string,
                            pageNumber: number, pageSize: number, needCount: boolean, hasPermission: boolean): void {
    if(hasPermission) {
      this.userFilterService.getDataSourceUserFilters(workspaceId, dataSourceId, searchKey,
          pageNumber, pageSize, needCount).subscribe(
          (response) => {
            if(needCount) {
              this.dataSourceState.setUserFiltersTotalPages(response.data.totalPages);
            }
            this.dataSourceState.setUserFiltersPageNumber(pageNumber);
            this.dataSourceState.updateUserFilters(response.data.pageData);
            this.dataSourceState.loadingUserFilters = false;
            this.dataSourceState.checkDataSourceIsLoaded();
          },
          (errorRes) => {
            this.toastr.showError(errorRes.error.message);
          },
      );
    } else {
        this.dataSourceState.setUserFiltersTotalPages(0);
        this.dataSourceState.setUserFiltersPageNumber(1);
        this.dataSourceState.loadingUserFilters = false;
        this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  getUserFilters$(): Observable<Partial<UserFilter>[]> {
    return this.dataSourceState.getUserFilters$();
  }

  removeUseFilter(userFilter: Partial<UserFilter>): void {
      this.setSubmitState('submitting');
      this.userFilterService.removeUserFilter(userFilter).subscribe(
          () => {
              this.setSubmitState('submitted');
              this.toastr.showSuccess('تغییرات با موفقیت ذخیره شد.');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadUserAndGroupToAddUserFilter(userFilterId: string | undefined): void {
    this.userFilterService.getUserAndGroupToAddUserFilter(userFilterId,
        this.dataSourceState.getWorkspaceId(), this.dataSourceState.getDataSourceId()).subscribe(
        (response) => {
          this.dataSourceState.setUsersAndGroupsToAddUserFilter(response.data.usersAndUserGroups);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
    );
  }

  getUsersAndGroups$(): Observable<UserOrGroup[]> {
    return this.dataSourceState.getUsersAndGroups$();
  }

  loadColumnValues(columnId: string, selectedValues: string[], paginationValues: any, appendValues: boolean = false): void {
      this.userFilterService.getColumnValues(columnId, selectedValues, paginationValues,
          this.dataSourceState.getWorkspaceId(), this.dataSourceState.getDataSourceId()).subscribe(
          (response) => {
              if (!appendValues) {
                  this.dataSourceState.setSelectedColumnValues(response.data.data);
              } else {
                  this.dataSourceState.appendSelectedColumnValues(response.data.data);
              }
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getSelectedColumnValues$(): Observable<string[]> {
      return this.dataSourceState.getSelectedColumnValues$();
  }

  createUserFilter(columnId: string, name: string, updateUserInUserFilter:  Partial<UserInUserFilter>[]): void {
      this.setSubmitState('submitting');
      const updateUserFilterRecipients = this.dataSourceState.getUserFilterRecipients(updateUserInUserFilter, 'Create');
      this.userFilterService.createUseFilter(columnId, name, updateUserFilterRecipients,
          this.dataSourceState.getWorkspaceId(),
          this.dataSourceState.getDataSourceId()).subscribe(
          () => {
              this.loadDataSourceUserFilters(this.dataSourceState.getWorkspaceId(), this.dataSourceState.getDataSourceId(),
                  '', 1,9, true, true);
              this.setSubmitState('submitted');
              this.toastr.showSuccess('فیلتر کاربری ایجاد شد.');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  };

  loadUserFilterDetails(userFilter: Partial<UserFilter>, userFilterType: 'Dashboard' | 'Chart' = undefined): void {
    this.dataSourceState.isUserFilterDetailsError.set(false);
      this.userFilterService.getDataSourceUserFilterDetails(userFilter).subscribe(
          (response) => {
            this.dataSourceState.setUserFilterDetails(response.data);
          },
          (errorRes) => {
            this.dataSourceState.isUserFilterDetailsError.set(true);
            this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getUserFilterDetails$(): Observable<UserFilterDetails> {
      return this.dataSourceState.getUserFilterDetails$();
  }

  getSelectedUsersInUserFilter$(): Observable<Partial<UserInUserFilter>[]> {
      return this.dataSourceState.getSelectedUsersInUserFilter$();
  }

  updateUserFilter(userFilterId: string, name: string, columnId: string,
                   updateUserInUserFilter: Partial<UserInUserFilter>[]) {
      this.setSubmitState('submitting');
      const updateUserFilterRecipients = this.dataSourceState.getUserFilterRecipients(updateUserInUserFilter, 'Edit');
      this.userFilterService.updateUseFilter(userFilterId,columnId, name, updateUserFilterRecipients,
          this.dataSourceState.getWorkspaceId(), this.dataSourceState.getDataSourceId()).subscribe(
          () => {
              this.loadDataSourceUserFilters(this.dataSourceState.getWorkspaceId(), this.dataSourceState.getDataSourceId(),
                  '', 1,9, true, true);
              this.setSubmitState('submitted');
              },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  addUserToUserFilter(userOrGroup: UserOrGroup) {
      this.dataSourceState.addUserToUserFiler(userOrGroup);
  }

  removeUserOrGroup(userInUserFilter: Partial<UserInUserFilter>) {
      this.dataSourceState.removeUserFromUserFilter(userInUserFilter);
  }

  resetUserFilterDetails(): void {
      this.dataSourceState.setUserFilterDetails(undefined);
  }

  resetSelectedUsersOrGroupsInUserFilter(): void {
      this.dataSourceState.setSelectedUserSInUserFilter([]);
  }

  resetSelectedColumnValues(): void {
      this.dataSourceState.setSelectedColumnValues([]);
  }

  // schedule
  loadSchedule(assetId: string,workspaceId: string, permissionViewScheduleIsChecked: boolean = true): void {
    if(permissionViewScheduleIsChecked) {
      this.dataSourceApi.getScheduleOfAsset(assetId,workspaceId).pipe(map(res => res.data)).subscribe(
        (schedule: ScheduleAsset) => {
          this.dataSourceState.updateSchedule(schedule);
        },
        (err) => {
          if(err.error.statusCode === 3) {
            this.dataSourceState.updateSchedule(null);
          } else {
            this.toastr.showError(err.error.message);
          }
        },
        () => {
        },
      );
    } else {
      this.dataSourceState.loadingSchedule = false;
      this.dataSourceState.updateSchedule({});
      this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  loadScheduleHistory(workspaceId: string, datasourceId: string,pageNumber: number,
                      pageSize: number, needCount: boolean, permissionViewScheduleIsChecked: boolean = true): void {
    if(permissionViewScheduleIsChecked) {
      this.dataSourceApi.getScheduleHistory(workspaceId, datasourceId, pageNumber, pageSize,needCount)
        .pipe(map((res: any) => res.data)).subscribe(
        (res: any) => {
          if(needCount) {
            this.dataSourceState.setScheduleHistoryTotalPages(res.totalPages);
          }
          this.dataSourceState.setScheduleHistoryPageNumber(pageNumber);
          this.dataSourceState.updateScheduleHistory(res.pageData);
          this.dataSourceState.loadingScheduleHistory = false;
          this.dataSourceState.checkDataSourceIsLoaded();
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
        () => {},
      );
    } else {
      this.dataSourceState.updateScheduleHistory([]);
      this.dataSourceState.setScheduleHistoryTotalPages(0);
      this.dataSourceState.setScheduleHistoryPageNumber(0);
      this.dataSourceState.loadingScheduleHistory = false;
      this.dataSourceState.checkDataSourceIsLoaded();
    }
  }

  addSchedule(newSchedule: ScheduleAsset,workspaceId: string,scheduleAction: NbDialogRef<any>): void {
    this.dataSourceApi.addSchedule(newSchedule).subscribe(
      () => {
        this.loadSchedule(newSchedule.assetId,workspaceId);
        this.toastr.showSuccess('زمانبندی منبع داده با موفقیت تعریف شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        scheduleAction.close();
      },
    );
  }

  updateSchedule(newSchedule: ScheduleAsset,workspaceId: string, scheduleAction: NbDialogRef<any>): void {
    this.dataSourceApi.updateSchedule(newSchedule).subscribe(
      (res) => {
        this.loadSchedule(newSchedule.assetId, workspaceId);
        this.toastr.showSuccess('زمانبندی منبع داده با موفقیت ویرایش شد.' );
        scheduleAction.close();
      },
      () => {
        this.toastr.showError('ویرایش زمانبندی منبع داده ناموفق بود.' );
        scheduleAction.close();
      },
    );
  }

  deactivationSchedule(schedule: ScheduleAsset,workspaceId: string, scheduleAlertDialog: NbDialogRef<any>): void {
    this.dataSourceApi.deactivationSchedule(schedule).subscribe(
      () => {
        this.loadSchedule(schedule.assetId,workspaceId);
        scheduleAlertDialog.close();
        this.toastr.showSuccess('\"' + schedule.title + '\" غیر فعال شد.');
      },
      (errorRes) => {
        scheduleAlertDialog.close();
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  activationSchedule(schedule: ScheduleAsset,workspaceId: string, scheduleAlertDialog: NbDialogRef<any>): void {
    this.dataSourceApi.activationSchedule(schedule).subscribe(
      () => {
        this.loadSchedule(schedule.assetId,workspaceId);
        scheduleAlertDialog.close();
        this.toastr.showSuccess('\"' + schedule.title + '\" فعال شد.');
      },
      (errorRes) => {
        scheduleAlertDialog.close();
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  getScheduleOfAsset$(): Observable<ScheduleAsset> {
    return this.dataSourceState.getScheduleOfAsset$();
  }

  getScheduleHistory$(): Observable<ScheduleHistory[]> {
    return this.dataSourceState.getScheduleHistory$();
  }

  //calculate column
  getCalculateColumnSubmit$(){
    return this.dataSourceState.getCalculateColumnSubmit$();
  }

  createCalculateColumn(calculateColumn: Partial<CalculateColumnModel>, dashboardId = undefined){
    const _calculateColumn: Partial<CalculateColumnModel> = {
      workspaceId: calculateColumn.workspaceId,
      dataSourceId: calculateColumn.dataSourceId,
      columnName: calculateColumn.columnName,
      expression: calculateColumn.expression,
      dashboardId,
    };
    this.dataSourceApi.createCalculateColumn(_calculateColumn).subscribe(
      ()=> {
        this.toastr.showSuccess();
        this.dataSourceState.updateCalculateColumnSubmit('submitted');
        this.dataSourceState.updateCalculateColumnError(null);
        this.dataSourceApi.getDataSourceData(calculateColumn.dataSourceId, calculateColumn.workspaceId, 'Hundred').subscribe(
          (res)=>{
              this.dataSourceState.updateDataSourceTableContent(res.data.columns);
              this.loadDatasourceColumnsDataTypes(calculateColumn.workspaceId, calculateColumn.dataSourceId);
          },
        );
      },
      (errorRes) => {
        this.dataSourceState.updateCalculateColumnSubmit('submittedWithError');
        this.dataSourceState.updateCalculateColumnError(errorRes.error.message);
      },
    );
  }

  updateCalculateColumn(calculateColumn: Partial<CalculateColumnModel>, dashboardId = undefined){
    const _calculateColumn: Partial<CalculateColumnModel> = {
      workspaceId: calculateColumn.workspaceId,
      dataSourceId: calculateColumn.dataSourceId,
      columnName: calculateColumn.columnName,
      expression: calculateColumn.expression,
      columnId: calculateColumn.columnId,
      dashboardId,
    };
    this.dataSourceApi.updateCalculateColumn(_calculateColumn).subscribe(
      ()=> {
        this.toastr.showSuccess();
        this.dataSourceState.updateCalculateColumnSubmit('submitted');
        this.dataSourceState.updateCalculateColumnError(null);
        this.dataSourceApi.getDataSourceData(calculateColumn.dataSourceId, calculateColumn.workspaceId, 'Hundred').subscribe(
          (res)=>{
            this.dataSourceState.updateDataSourceTableContent(res.data.columns);
            this.loadDatasourceColumnsDataTypes(calculateColumn.workspaceId, calculateColumn.dataSourceId);
          },
        );
      },
      (err) => {
        this.dataSourceState.updateCalculateColumnSubmit('submittedWithError');
        this.dataSourceState.updateCalculateColumnError(err.error.message);
      },
    );
  }

  deleteCalculateColumn(columnId: string, workspaceId: string, dataSourceId: string){
    this.dataSourceApi.deleteCalculateColumn(columnId, workspaceId, dataSourceId).subscribe(
      ()=>{
        this.dataSourceState.deleteCustomColumn(columnId);
      },
      (errorRes)=>{
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getEditCalculateColumn$(): Observable<Partial<CalculateColumnModel>>{
    return this.dataSourceState.getEditCalculateColumn$();
  }

  getCalculateColumnError$(){
    return this.dataSourceState.getCalculateColumnError$();
  }

  updateCalculateColumnError(error: string){
    this.dataSourceState.updateCalculateColumnError(error);
  }

  loadCalculateColumnExpression(column: Column, workspaceId: string, dataSourceId: string) {
    this.dataSourceApi.getCalculateColumnExpression(column.id, workspaceId, dataSourceId).subscribe(
      (res) => {
        const editColumn: Partial<CalculateColumnModel> = {
          columnId: column.id,
          columnName: column.name,
          dataSourceId,
          workspaceId,
          expression: res.data.expression,
        };
        this.dataSourceState.updateEditCalculateColumn(editColumn);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  // relations
  loadDataSourcesForRelation(bodyRequest: any) {
    this.dataSourceApi.getDataSourcesForRelation(bodyRequest).subscribe(
      (res) => {
        const dataSources: Partial<DataSource>[] = res.data.pageData;
        if(bodyRequest.needCount) {
          this.dataSourceState.setTotalPagesRelation(res.data.totalPages);
        }
        this.dataSourceState.setPageNumberRelation(bodyRequest.pageNumber);
        this.dataSourceState.convertDataSourcesSizeToKiloByte(dataSources);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  getDataSourcesForRelation$(): Observable<Partial<DataSource>[]> {
    return this.dataSourceState.getDataSourcesForRelation$();
  }

  getDataSourceTableContent(dataSourceId, workspaceId: string, rowNumber: DataSourceRowNumber) {
    return this.dataSourceApi.getDataSourceData(dataSourceId,workspaceId,rowNumber).
    pipe(map(res => res.data.columns));
  }

  createRelation(relation: Relation) {
    this.dataSourceState.updateCreateRelationSubmitState(Submit.submitting);
    this.dataSourceApi.createRelation(relation).subscribe(
      (response)=>{
        this.dataSourceState.createRelation(relation,response.data.relationId);
        this.dataSourceState.updateCreateRelationSubmitState(Submit.submitted);
        this.toastr.showSuccess('ارتباط ساخته شد.');
      },
      (errorRes)=>{
        this.toastr.showError(errorRes.error.message);
        this.dataSourceState.updateCreateRelationSubmitState(Submit.notSubmitted);
      },
    );
  }

  updateRelation(relation: Relation) {
    this.dataSourceState.updateCreateRelationSubmitState(Submit.submitting);
    this.dataSourceApi.updateRelation(relation).subscribe(
      () => {
        this.dataSourceState.updateRelation(relation);
        this.dataSourceState.updateCreateRelationSubmitState(Submit.submitted);
        this.toastr.showSuccess();
      },
      (errorRes)=>{
        this.toastr.showError(errorRes.error.message);
        this.dataSourceState.updateCreateRelationSubmitState(Submit.notSubmitted);
      },
    );
  }

  activateRelation(relation: Relation, workspaceId: string) {
    this.setSubmitState('submitting');
    this.dataSourceApi.activateRelation(relation, workspaceId).subscribe(
      () => {
        this.dataSourceState.toggleActivationRelation(relation,false);
        this.toastr.showSuccess('ارتباط فعال شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  deactivateRelation(relation: Relation, workspaceId: string) {
    this.setSubmitState('submitting');
    this.dataSourceApi.deactivateRelation(relation, workspaceId).subscribe(
      () => {
        this.dataSourceState.toggleActivationRelation(relation,true);
        this.toastr.showSuccess('ارتباط غیرفعال شد.');
      },
      (errorRes) => {
        this.dataSourceState.toggleActivationRelation(relation,false);
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  updateCreateRelationSubmitState(submit: Submit) {
    this.dataSourceState.updateCreateRelationSubmitState(submit);
  }

  getCreateRelationSubmit$(): Observable<Submit> {
    return this.dataSourceState.getCreateRelationSubmit$();
  }

  getDataSource$(): Observable<DataSource> {
    return this.dataSourceState.getDataSource$();
  }

  addToRelationsModel(workspaceId: string, sourceDataSourceId: string, destinationDataSourceId: string): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.addToRelationsModel(workspaceId, sourceDataSourceId, destinationDataSourceId).
      pipe(map(res => res.data)).subscribe(
      (dataSource: Model) => {
        this.dataSourceState.addDataSourceInRelationDataInModels(dataSource);
        this.toastr.showSuccess('منبع داده اضافه شد.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
        },
      () => {},
    );
  }

  deleteDataSourceFromModel(workspaceId: string, sourceDataSourceId: string, destinationDataSourceId: string): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.deleteDataSourceFromModel(workspaceId, sourceDataSourceId, destinationDataSourceId).subscribe(
      () => {
        this.dataSourceState.deleteDataSourceFromRelationData(destinationDataSourceId);
        this.toastr.showSuccess('عملیات حذف موفق بود.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
        },
      () => {},
    );
  }

  deleteRelation(workspaceId: string, relationId: string,
                 sourceDataSourceId: string, destinationDataSourceId: string): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.deleteRelation(workspaceId,relationId,
      sourceDataSourceId, destinationDataSourceId).subscribe(
      () => {
        this.dataSourceState.deleteRelation(relationId);
        this.toastr.showSuccess('عملیات حذف موفق بود.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
        },
      () => {},
    );

  }

  getTotalPagesRelation$(): Observable<number> {
    return this.dataSourceState.getTotalPagesRelation$();
  }

  getPageNumberRelation$(): Observable<number> {
    return this.dataSourceState.getPageNumberRelation$();
  }

  setRelationState(relationState: RelationState): void {
    this.dataSourceState.setRelationState(relationState);
  }

  getRelationState$(): Observable<RelationState> {
    return this.dataSourceState.getRelationState$();
  }

  // webService
  loadWebServiceConnection(workspaceId: string, dataSourceId: string) {
    this.dataSourceApi.getWebServiceConnection(workspaceId,dataSourceId).pipe(map(res => res.data)).subscribe(
      (webServiceConnection: WebServiceConnection) => {
        this.dataSourceState.updateWebServiceConnection(webServiceConnection);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateWebServiceConnection(newWebServiceConnection: any): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.updateWebServiceConnection(newWebServiceConnection).subscribe(
      () => {
        this.dataSourceState.setDataSourceName(newWebServiceConnection.name);
        this.setSubmitState('submitted');
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  getWebServiceConnection$(): Observable<WebServiceConnection> {
    return this.dataSourceState.getWebServiceConnection$();
  }

  // conditional column
  loadColumnList(): void {
    this.dataSourceState.loadColumnList();
  }

  getColumnList$(): Observable<ColumnList[]> {
    return this.dataSourceState.getColumnList$();
  }

  loadConditionalColumn(dataSourceId: string, workspaceId: string, columnId: string): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.getConditionalColumn(dataSourceId, workspaceId, columnId).subscribe(
      (res) => {
        this.setSubmitState('notSubmitted');
        this.dataSourceState.setConditionalColumn(res.data);
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getConditionalColumn$(): Observable<DataSourceConditionalColumn> {
    return this.dataSourceState.getConditionalColumn$();
  }

  createConditionalColumn(conditionalColumn: Partial<DataSourceConditionalColumn>): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.createConditionalColumn(conditionalColumn).subscribe(
      () => {
        this.setSubmitState('submitted');
        this.toastr.showSuccess();
        this.dataSourceApi.getDataSourceData(conditionalColumn.dataSourceId,
          conditionalColumn.workspaceId, 'Hundred').subscribe(
            (res) => {
              this.dataSourceState.updateDataSourceTableContent(res.data.columns);
              this.loadDatasourceColumnsDataTypes(conditionalColumn.workspaceId, conditionalColumn.dataSourceId);
            },
          );
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateConditionalColumn(conditionalColumn: Partial<DataSourceConditionalColumn>): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.updateConditionalColumn(conditionalColumn).subscribe(
      () => {
        this.setSubmitState('submitted');
        this.toastr.showSuccess();
        this.dataSourceApi.getDataSourceData(conditionalColumn.dataSourceId,
          conditionalColumn.workspaceId, 'Hundred').subscribe(
            (res) => {
              this.dataSourceState.updateDataSourceTableContent(res.data.columns);
              this.loadDatasourceColumnsDataTypes(conditionalColumn.workspaceId, conditionalColumn.dataSourceId);
            },
          );
      },
      (errorRes) => {
        this.setSubmitState('notSubmitted');
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteConditionalColumn(columnId: string, workspaceId: string, dataSourceId: string) {
    this.dataSourceApi.deleteConditionalColumn(columnId, workspaceId, dataSourceId).subscribe(
      () => {
        this.dataSourceState.deleteCustomColumn(columnId);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  /**
   *
   * @param dataSourceId
   * @param columnIds
   * load custom dates
   *
   */
  loadCustomDateColumnsData(dataSourceId: string ,columnIds: string[]): void {
    const sources = [];
    columnIds.forEach(id => {
      sources.push(this.dataSourceApi.getCustomDateColumns(dataSourceId, id));
    });
    forkJoin(sources).subscribe(
      (res) => {
        if(res[0]['data']){
          this.dataSourceState.setCustomDateColumnsData(res[0]['data'].customDateColumns);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  setDateColumns(dataSourceId: string, columnId: string, customDateColumns: CustomDateColumns[], workspaceId: string): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.setCustomDateColumns(dataSourceId, columnId, customDateColumns).subscribe(
      (res) => {
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        this.createCustomDateColumns(dataSourceId,columnId, workspaceId);
      },
    );
  }

  createCustomDateColumns(dataSourceId: string, columnId: string, workspaceId: string){
    this.dataSourceApi.createCustomDateColumns(dataSourceId,columnId).subscribe(
      results=>{
        this.dataSourceApi.getDataSourceData(
          dataSourceId,
          workspaceId,
          'Hundred').subscribe(
            (res) => {
              this.dataSourceState.updateDataSourceTableContent(res.data.columns);
              this.setSubmitState('submitted');
            },
          );
      },
    );
  }

  deleteCustomDateColumn(columnId: string, dataSourceId: string ,workspaceId: string) {
    this.setSubmitState('submitting');
    this.dataSourceApi.deleteCustomDateColumn(columnId, dataSourceId, workspaceId).subscribe(
      () => {
        this.dataSourceState.deleteCustomColumn(columnId);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateCustomDateColumnName(columnId: string, dataSourceId: string, workspaceId: string, name: string){
    this.setSubmitState('submitting');
    this.dataSourceApi.updateCustomDateColumnName(columnId, dataSourceId, workspaceId, name ).subscribe(
      () => {
        this.dataSourceApi.getDataSourceData(dataSourceId, workspaceId, 'Hundred').subscribe(
          (res) => {
            this.dataSourceState.updateDataSourceTableContent(res.data.columns);
            this.loadDatasourceColumnsDataTypes(workspaceId, dataSourceId);
            this.setSubmitState('submitted');
          },
        );
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }
  // defaults
  getLoadingDataSource$(): Observable<boolean> {
    return this.dataSourceState.getLoadingDataSource$();
  }
  updateWorkspaceId(workspaceId: string): void {
    this.dataSourceState.updateWorkspaceId(workspaceId);
  }

  getWorkspaceId$(): Observable<string> {
    return this.dataSourceState.getWorkspaceId$();
  }

  updateDataSourceId(dataSourceId: string): void {
    this.dataSourceState.updateDataSourceId(dataSourceId);
  }

  getDataSourceId$(): Observable<string> {
    return this.dataSourceState.getDataSourceId$();
  }

  getScheduleHistoryPageNumber$(): Observable<number> {
    return this.dataSourceState.getScheduleHistoryPageNumber$();
  }

  getScheduleHistoryTotalPages$(): Observable<number> {
    return this.dataSourceState.getScheduleHistoryTotalPages$();
  }

  getUserFiltersPageNumber$(): Observable<number> {
      return this.dataSourceState.getUserFiltersPageNumber$();
  }

  getUserFiltersTotalPages$(): Observable<number> {
      return this.dataSourceState.getUserFiltersTotalPages$();
  }

  setSubmitState(submitted: SubmitType): void {
    this.dataSourceState.setSubmitState(submitted);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.dataSourceState.getSubmitState$();
  }

  toggleMainDataSourceShowAllFields(): void {
      this.dataSourceState.toggleMainDataSourceShowAllFields();
  }

  makeNewDashboard(newDashboard: any, workspaceId: string) {
    this.setSubmitState('submitting');
    newDashboard.dashboardDesignSettings = `{"backgroundColor": "#F8F8F8",
                                       "showTitle": true,
                                       "titleBackgroundColor": "#FFFFFF",
                                       "titleColor": "#4D4D4D",
                                       "titleDividerLineColor": "#EDEDED",
                                       "titleFontFamily": "iranyekan",
                                       "titleFontSize": 24,
                                       "toggleFooter": false,
                                       "autoRefreshEnabled": false,
                                       "textFooterColor": "#4D4D4D",
                                       "textFooterAlignment": "Right",
                                       "footerBackground": "#FFFFFF",
                                       "footerBorder": "#FFFFFF",
                                       "footerDividerColor": "#E8E8E8",
                                       "showCreator": false,
                                       "showLastModifiedDataSourceUpdated": false,
                                       "showLastModifiedDashboardUpdated": false,
                                       "showView": false,
                                       "showUsersCountAccessToDashboard": false,
                                       "showCustomText": false,
                                       "footerCustomText": "",
                                       "chipsColor": "#00000099",
                                       "chipsBackgroundColor": "#dddddd",
                                       "version": "0.0.1"}`;
    this.dataSourceApi.makeNewDashboard(newDashboard).subscribe(
      (res) => {
        this.router.navigate([`/dashboard/view/${workspaceId}/${res.data.dashboardId}`]);
        this.toastr.showSuccess('داشبورد ساخته شد.');
        this.setSubmitState('notSubmitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('notSubmitted');
      },
    );
  }

  addBookmark(dataSource: DataSource): void{
    dataSource.isBookmarked = true;
    this.dataSourceState.updateMainDataSource(dataSource);
    this.dataSourceApi.addBookmark(dataSource.id, dataSource.workspaceId).subscribe(
      () =>{
      },
      (errorRes) => {
        dataSource.isBookmarked = false;
        this.dataSourceState.updateMainDataSource(dataSource);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  removeBookmark(dataSource: DataSource): void{
    dataSource.isBookmarked = false;
    this.dataSourceState.updateMainDataSource(dataSource);
    this.dataSourceApi.removeBookmark(dataSource.id, dataSource.workspaceId).subscribe(
      () => {
      },
      (errorRes) => {
        dataSource.isBookmarked = true;
        this.dataSourceState.updateMainDataSource(dataSource);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  deleteDataSource(dataSource: DataSource): void {
    this.dataSourceApi.deleteDataSource(dataSource.workspaceId, dataSource.id).subscribe(
      () => {
        this.dataSourceState.setSubmitState('submitted');
        this.dataSourceState.setUsedDashboards([]);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submittedWithError');
      },
    );
  }

  duplicateDatasource(dataSource: DataSource): void {
    const infoToster = this.toastr.showInfo(`در حال کپی منبع داده “${dataSource.name}”`, null);
    this.dataSourceApi.duplicateDatasource(dataSource.workspaceId, dataSource.id).subscribe(
      (res) => {
        this.toastr.removeToast(infoToster.toastId);

        if (res?.data?.hasError) {
          const errors = res.data.errorMessages?.filter((e) => !!e) || [];
          let message = 'برخی ستون‌ها در منبع‌داده جدید کپی نمی‌شوند.<br>';

          if (errors.length > 0) {
            errors.forEach((err, index) => {
              message += `${index + 1}. ${err}<br>`;
            });
          }

          message += 'لطفاً در صورت نیاز آن‌ها را مجدداً ایجاد کنید.';

          const dialogRef = this.dialogService.open(MessageDialogComponent, {
            closeOnBackdropClick: false,
            context: {
              icon: 'warning',
              title: 'اخطار',
              dialogMode: 'simple-warning',
              message,
            },
          });

          dialogRef.onClose.subscribe(() => {
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigate([`/datasource/${dataSource.workspaceId}/${res.data.dataSourceId}`]);
            });
          });
        } else {
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([`/datasource/${dataSource.workspaceId}/${res.data.dataSourceId}`]);
          });
        }
      },
      (errorRes) => {
        this.toastr.removeToast(infoToster.toastId);
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  changeCompletionStatus(dataSource: DataSource): void {
    this.dataSourceApi.changeCompletionStatus(dataSource.workspaceId, dataSource.id).subscribe(
      (res) => {
        this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
          this.router.navigate([`/datasource/${dataSource.workspaceId}/${dataSource.id}`]);
        });
      });
  }

  changeDataSourceName(dataSource: DataSource): void {
    this.dataSourceApi.changeDataSourceName(dataSource.workspaceId, dataSource.id, dataSource.name).subscribe(
      () => {
        this.dataSourceState.updateMainDataSource(dataSource);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  createReplacementTable(previewDataSourceInfo: Partial<PreviewDataSourceInfo>, workspaceId: string,
    columnsTypes: ColumnsType, sheetName: string = undefined): void {
    this.setSubmitState('submitting');
    this.dataSourceApi.createReplacementTable(previewDataSourceInfo, workspaceId, columnsTypes, sheetName)
      .subscribe((response) => {
        if (!response.data.hasError) {
          this.toastr.showSuccess('تغییرات با موفقیت ذخیره شد.');
          this.setSubmitState('submitted');
          this.loadDataSourceTableContent(workspaceId, previewDataSourceInfo.dataSourceId, 'Hundred',
            true);
          this.loadDatasourceColumnsDataTypes(workspaceId, previewDataSourceInfo.dataSourceId, true);
        } else {
          this.setSubmitState('notSubmitted');
          this.dialogService.open(MessageDialogComponent,
            {
              closeOnBackdropClick: false,
              context: {
                message: 'بارگذاری انجام نشد. پس از بررسی جدول، دوباره امتحان کنید.',
                errorsList: response.data.errorMessages,
                icon: 'error',
                errorsListTitle: 'مقادیر ناهماهنگ(' + response.data.errorMessages.length + ')',
                title: 'ناهماهنگی در مقدار',
              },
            });
        }
      },
        (errorRes) => {
          if (errorRes.error.showPopUp) {
            this.dialogService.open(MessageDialogComponent,
              {
                closeOnBackdropClick: false,
                context: { message: 'جایگذاری با مشکل مواجه شد. نوع داده ستون با مقادیر آن، هماهنگ نیست.\n' +
                'پس از بررسی مقدار و نوع داده ستون‌ها، دوباره بارگذاری کنید.'
                , errorMessage: errorRes.error.message, icon: 'error', title: 'خطا' },
              });
          } else {
            this.toastr.showError(errorRes.error.message);
          }
          this.setSubmitState('notSubmitted');
        },
      );
  }
  previousProcessingDataSources: ProcessingDataSource[] = [];

  loadProcessingDataSources() {
    this.dataSourceApi.getProcessingDataSources().pipe(
      map(res => res.data)
    ).subscribe(
      (processingDataSources: ProcessingDataSource[]) => {
        this.dataSourceState.setProcessingDataSources(processingDataSources);
          const currentSize = processingDataSources.length;
        const previousSize = this.previousProcessingDataSources?.length || processingDataSources.length;

        if (currentSize > previousSize) {
          const newItems = processingDataSources.filter(
            currentItem =>
              !this.previousProcessingDataSources?.some(previousItem => previousItem.id === currentItem.id)
          );
            newItems.forEach(newItem => {
            newItem.isExpanded = true;
            newItem.isRedBoxExpanded = true;
          });
        }

        // Iterate over current data sources and check for transitions
        processingDataSources.forEach((item) => {
          const previousItem = this.previousProcessingDataSources?.find(p => p.id === item.id);

          if (previousItem) {
            // Handle transition from 'InProgress' to 'Completed' or 'HasError'
            if (previousItem.progressStatus === 'InProgress' && item.progressStatus === 'Completed') {
              if (previousItem.isDuplicated) {
                this.toastr.showSuccess(`تکرار منبع داده "${item.name}" کامل شد.`);
              } else {
                this.toastr.showSuccess(`بارگذاری منبع داده "${item.name}" کامل شد.`);
              }
            }
            if (previousItem.progressStatus === 'InProgress' && item.progressStatus === 'HasError') {
              if (previousItem.isDuplicated) {
                this.toastr.showError(`تکرار منبع داده "${item.name}" با مشکل مواجه شد.`);
              } else {
                this.toastr.showError(`بارگذاری منبع داده "${item.name}" با مشکل مواجه شد.`);
              }
            }
          }
        });

        // Store the current state as the previous state for the next comparison
        this.previousProcessingDataSources = [...processingDataSources];
      },
      (error) => {
        // Handle errors if needed
      }
    );
  }

  getProcessingDataSources$() {
    return this.dataSourceState.getProcessingDataSources$();
  }

  retryProcessing(dataSource: ProcessingDataSource): void {
    this.dataSourceApi.retryProcessing(dataSource).subscribe(
      (response) => {
        this.toastr.showSuccess()
        dataSource.needRetry = false;
      },
      (error) => {
      }
    );
  }

  getDataSourceUploadProgress() {
    return this.dataSourceState.getDataSourceUploadProgress();
  }

  updateDataSourceUploadProgress(progress: DataSourceUploadProgress) {
    if(progress.completionPercentage == 100) {
      this.progressService.updateCompletedDataSourceItem(progress);
    }
    this.dataSourceState.updateDataSourceUploadProgress(progress);
  }

  cancelDataSourceUploadProgress(workspaceId: string, dataSourceId: string, URL?: string) {
    this.dataSourceApi.cancelDataSourceUploadProgress(workspaceId, dataSourceId)
    .subscribe((res: any) => {
      this.dataSourceState.cancelDataSourceUploadProgress(dataSourceId)
      if(!!URL && URL.split(/[\/\?\#]/).includes(dataSourceId)) {
        this.router.navigate(['/home', workspaceId]);
      }
    })
  }

  getDataSourceUploadCanceledValue(): Observable<string | null> {
    return this.dataSourceState.getDataSourceCanceledSubject()
  }

  updateDataSourceUploadCanceledValue(value: string | null): void {
    this.dataSourceState.updateDataSourceCanceledSubject(value)
  }
}
